import heic2any from "heic2any";
import produce from "immer";
import { noop, omit } from "lodash";
import moment from "moment";
import { from, Observable, of } from "rxjs";
import { None, Option, Some } from "space-monad";
import { store } from "..";
import { withProgress } from "../../components/loader/file-progress";
import { ResultWithProgress } from "../../components/loader/models";
import { API } from "../../services/http";
import { Pageable } from "../../services/pageable";
import { BillingActions } from "../billing/actions";
import { BillingDTO } from "../billing/models/dto";
import { FetchableError, FetchableSuccess } from "../common/fetchable/helpers";
import { addFieldsToForm, flattenObj } from "../common/forms";
import { countries } from "../common/models";
import { generateDownload } from "../common/utils";
import { processErrorFromPromise } from "../errors/utils";
import { successNotificationFromPromise } from "../notifications/utils";
import { UserActions } from "../users/actions";
import { VerificationActions } from "./actions";
import { VerificationDTO } from "./models/dto";
import { VerificationDTO as AMLVerificationDTO } from "./models/dto/aml";
import { VerificationDTO as DueDiligenceVerificationDTO } from "./models/dto/due-diligence";
import { VerificationDTO as KYCVerificationDTO } from "./models/dto/kyc";
import { CertificateDetails } from "./models/internal";

export namespace VerificationService {
  const api = new API("verification");

  export function getChecksSummary(): Observable<
    | FetchableError
    | FetchableSuccess<KYCVerificationDTO.Responses.GetChecksSummary.T>
  > {
    var dateOffset = 5 * 7 * 24 * 60 * 60 * 1000; //4 weeks
    var startDate = new Date();
    startDate.setTime(startDate.getTime() - dateOffset);

    return from(
      api
        .get(`v1/checks/requests`)
        .query({ from: moment(startDate).format("yyyy-MM-DD") })
        .then<
          FetchableSuccess<KYCVerificationDTO.Responses.GetChecksSummary.T>
        >((response) => ({
          status: "ready",
          data: response.body,
        }))
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function postSelfKYC(
    fields: KYCVerificationDTO.Requests.UploadSelfKyc.T
  ): Observable<
    ResultWithProgress<KYCVerificationDTO.Responses.UploadIndividualKyc.T>
  > {
    const newFields: KYCVerificationDTO.Requests.UploadSelfKyc.T = {
      ...fields,
      backgroundChecks: { ongoingCheck: false },
    };
    return withProgress(
      addFieldsToForm(api.post(`v2/kyc/self`), flattenObj(newFields)),
      (r) =>
        r
          .responseType("json")
          .then(
            successNotificationFromPromise("kyc-check-submitted-for-processing")
          )
          .then((response) => response.body)
          .then((body) => {
            store.dispatch(VerificationActions.resetChecksSummary());
            store.dispatch(VerificationActions.resetAllSelfKyc());
            store.dispatch(UserActions.getUserDetails());
            return body;
          })
          .catch<KYCVerificationDTO.Responses.UploadIndividualKyc.T>(
            processErrorFromPromise
          )
    );
  }

  export function postIndividualKYC(
    fields: KYCVerificationDTO.Requests.UploadIndividualKyc.T
  ): Observable<
    ResultWithProgress<KYCVerificationDTO.Responses.UploadIndividualKyc.T>
  > {
    return withProgress(
      addFieldsToForm(api.post(`v2/kyc/internal`), flattenObj(fields)),
      (r) =>
        r
          .responseType("json")
          .then(
            successNotificationFromPromise("kyc-check-submitted-for-processing")
          )
          .then((response) => response.body)
          .then((body) => {
            store.dispatch(VerificationActions.resetChecksSummary());
            store.dispatch(BillingActions.resetVerificationBilling());
            store.dispatch(BillingActions.resetBalance());
            store.dispatch(BillingActions.resetAllUsages());
            store.dispatch(VerificationActions.resetAllKyc());
            return body;
          })
          .catch<KYCVerificationDTO.Responses.UploadIndividualKyc.T>(
            processErrorFromPromise
          )
    );
  }

  export function postBusinessKYB(
    fields: KYCVerificationDTO.Requests.UploadBusinessKyb.T
  ): Promise<KYCVerificationDTO.Responses.UploadBusinessKyb.T> {
    return api
      .post(`v1/kyb/`)
      .send(fields)
      .responseType("json")
      .then(successNotificationFromPromise("kyb-check-successful"))
      .then((response) => response.body)
      .then((body) => {
        store.dispatch(VerificationActions.resetChecksSummary());
        store.dispatch(BillingActions.resetVerificationBilling());
        store.dispatch(BillingActions.resetBalance());
        store.dispatch(BillingActions.resetAllUsages());
        store.dispatch(VerificationActions.resetAllKyb());
        store.dispatch(VerificationActions.resetAllBusinessAml());
        return body;
      })
      .catch<KYCVerificationDTO.Responses.UploadBusinessKyb.T>(
        processErrorFromPromise
      );
  }

  export function getAllKyc(
    pagination: Pageable.T<
      | "firstName"
      | "lastName"
      | "country"
      | "emailAddress"
      | "userStatus"
      | "createdDate"
      | "fraudCheckId"
    > = {},
    self?: boolean
  ): Observable<
    FetchableError | FetchableSuccess<KYCVerificationDTO.Responses.GetAllKyc.T>
  > {
    return from(
      api
        .post(`v2/kyc/search`)
        .query({
          ...Pageable.getQueryParam(omit(pagination, "filters")),
          ...(self !== undefined ? { showSelf: self } : {}),
        })
        .send(
          Option(pagination.filters)
            .map((f) => ({
              ...f
                .filter(({ field }) => field !== "userStatus")
                .reduce(
                  (acc, curr) => ({ ...acc, [curr.field]: curr.value }),
                  {}
                ),
              ...Option(f)
                .map((y) => y.find((x) => x.field === "userStatus"))
                .map((y) => y.value)
                .map<{}>((y) => ({ statuses: [y] }))
                .getOrElse({}),
            }))
            .getOrElse({})
        )
        .then<FetchableSuccess<KYCVerificationDTO.Responses.GetAllKyc.T>>(
          (response) => ({
            status: "ready",
            data: produce<KYCVerificationDTO.Responses.GetAllKyc.T>(
              response.body,
              (state) => {
                state.content = state.content.map((x: any) =>
                  KYCVerificationDTO.Responses.GetSingleKyc.parse(x)
                );
              }
            ),
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getSingleKyc(
    fraudCheckId: KYCVerificationDTO.Responses.GetSingleKyc.T["fraudCheckId"]
  ): Promise<KYCVerificationDTO.Responses.GetSingleKyc.T> {
    return api
      .get(`v2/kyc/${fraudCheckId}`)
      .then<KYCVerificationDTO.Responses.GetSingleKyc.T>((response) => {
        const result: any = produce(response.body, (body: any) => {
          if (body.user.address) {
            body.user.address.country = body.user.address.country
              ? (body.user.address.country.toUpperCase() as typeof countries.T)
              : body.user.address.country;
          }
        });

        const newResult = KYCVerificationDTO.Responses.GetSingleKyc.parse(
          result
        );

        store.dispatch(
          VerificationActions.setSingleKyc({
            fraudCheckId,
            result: newResult,
          })
        );
        return newResult;
      })
      .catch<KYCVerificationDTO.Responses.GetSingleKyc.T>(
        processErrorFromPromise
      );
  }

  export function getAllKyb(
    pagination: Pageable.T<
      keyof KYCVerificationDTO.Responses.GetSingleKyb.T
    > = {}
  ): Observable<
    FetchableError | FetchableSuccess<KYCVerificationDTO.Responses.GetAllKyb.T>
  > {
    return from(
      api
        .get(`v1/kyb`)
        .query(Pageable.getQueryParam(pagination))
        .then<FetchableSuccess<KYCVerificationDTO.Responses.GetAllKyb.T>>(
          (response) => ({
            status: "ready",
            data: response.body,
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getSingleKyb(
    fraudCheckId: KYCVerificationDTO.Responses.GetSingleKyb.T["fraudCheckId"]
  ): Promise<KYCVerificationDTO.Responses.GetSingleKyb.T> {
    return api
      .get(`v1/kyb/${fraudCheckId}`)
      .then<KYCVerificationDTO.Responses.GetSingleKyb.T>((response) => {
        const result: any = produce(response.body, (body: any) => {
          body.country = body.country
            ? (body.country.toUpperCase() as typeof countries.T)
            : body.country;
        });

        store.dispatch(
          VerificationActions.setSingleKyb({
            fraudCheckId,
            result,
          })
        );
        return result;
      })
      .catch<KYCVerificationDTO.Responses.GetSingleKyb.T>(
        processErrorFromPromise
      );
  }

  export function postIndividualAml(
    fields: AMLVerificationDTO.Requests.UploadIndividualAml.T
  ): Promise<AMLVerificationDTO.Responses.UploadIndividualAml.T> {
    return api
      .post(`v1/aml/individual`)
      .send(fields)
      .responseType("json")
      .then(successNotificationFromPromise("aml-check-successful"))
      .then((response) => response.body)
      .then((body) => {
        store.dispatch(VerificationActions.resetAllIndividualAml());
        store.dispatch(BillingActions.resetVerificationBilling());
        store.dispatch(BillingActions.resetBalance());
        store.dispatch(BillingActions.resetAllUsages());
        return body;
      })
      .catch<AMLVerificationDTO.Responses.UploadIndividualAml.T>(
        processErrorFromPromise
      );
  }

  export function postBusinessAml(
    fields: AMLVerificationDTO.Requests.UploadBusinessAml.T
  ): Promise<AMLVerificationDTO.Responses.UploadBusinessAml.T> {
    return api
      .post(`v1/aml/business`)
      .send(fields)
      .responseType("json")
      .then(successNotificationFromPromise("aml-check-successful"))
      .then((response) => response.body)
      .then((body) => {
        store.dispatch(VerificationActions.resetAllBusinessAml());
        store.dispatch(BillingActions.resetVerificationBilling());
        store.dispatch(BillingActions.resetBalance());
        store.dispatch(BillingActions.resetAllUsages());
        return body;
      })
      .catch<AMLVerificationDTO.Responses.UploadBusinessAml.T>(
        processErrorFromPromise
      );
  }

  export function getAllIndividualAml(
    pagination: Pageable.T<
      keyof AMLVerificationDTO.Responses.GetSingleIndividualAml.T
    > = {}
  ): Observable<
    | FetchableError
    | FetchableSuccess<AMLVerificationDTO.Responses.GetAllIndividualAml.T>
  > {
    return from(
      api
        .post(`v1/aml/individual/search`)
        .query(Pageable.getQueryParam(omit(pagination, "filters")))
        .send(
          Option(pagination.filters)
            .map((f) =>
              f.reduce(
                (acc, curr) => ({ ...acc, [curr.field]: curr.value }),
                {}
              )
            )
            .getOrElse({})
        )
        .then<
          FetchableSuccess<AMLVerificationDTO.Responses.GetAllIndividualAml.T>
        >((response) => ({
          status: "ready",
          data: response.body,
        }))
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getSingleIndividualAml(
    fraudCheckId: AMLVerificationDTO.Responses.GetSingleIndividualAml.T["fraudCheckId"]
  ): Promise<AMLVerificationDTO.Responses.GetSingleIndividualAml.T> {
    return api
      .get(`v1/aml/individual/${fraudCheckId}`)
      .then<AMLVerificationDTO.Responses.GetSingleIndividualAml.T>(
        (response) => {
          store.dispatch(
            VerificationActions.setSingleIndividualAml({
              fraudCheckId,
              result: response.body,
            })
          );
          return response.body;
        }
      )
      .catch<AMLVerificationDTO.Responses.GetSingleIndividualAml.T>(
        processErrorFromPromise
      );
  }

  export function getAllBusinessAml(
    pagination: Pageable.T<
      keyof AMLVerificationDTO.Responses.GetSingleBusinessAml.T
    > = {}
  ): Observable<
    | FetchableError
    | FetchableSuccess<AMLVerificationDTO.Responses.GetAllBusinessAml.T>
  > {
    return from(
      api
        .get(`v1/aml/business`)
        .query(Pageable.getQueryParam(pagination))
        .then<
          FetchableSuccess<AMLVerificationDTO.Responses.GetAllBusinessAml.T>
        >((response) => ({
          status: "ready",
          data: response.body,
        }))
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getSingleBusinessAml(
    fraudCheckId: AMLVerificationDTO.Responses.GetSingleBusinessAml.T["fraudCheckId"]
  ): Promise<AMLVerificationDTO.Responses.GetSingleBusinessAml.T> {
    return api
      .get(`v1/aml/business/${fraudCheckId}`)
      .then<AMLVerificationDTO.Responses.GetSingleBusinessAml.T>((response) => {
        store.dispatch(
          VerificationActions.setSingleBusinessAml({
            fraudCheckId,
            result: response.body,
          })
        );
        return response.body;
      })
      .catch<AMLVerificationDTO.Responses.GetSingleBusinessAml.T>(
        processErrorFromPromise
      );
  }

  // Billing

  export function getBilling(): Observable<
    FetchableError | FetchableSuccess<BillingDTO.Responses.GetBillingInfo.T>
  > {
    const login = store.getState().auth.login;
    if (login.status !== "ready")
      return of({
        status: "error",
        err: new Error("User not logged in"),
      });

    return from(
      api
        .get(`billing/${login.data.accessToken.credentialsId}/apis`)
        .then<FetchableSuccess<BillingDTO.Responses.GetBillingInfo.T>>(
          (response) => ({
            status: "ready",
            data: response.body,
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getKYBJurisdictions(): Observable<
    | FetchableError
    | FetchableSuccess<KYCVerificationDTO.Responses.GetKYBJurisdictions.T>
  > {
    return from(
      api
        .get(`v1/kyb/jurisdiction-codes`)
        .then<
          FetchableSuccess<KYCVerificationDTO.Responses.GetKYBJurisdictions.T>
        >((response) => ({
          status: "ready",
          data: response.body,
        }))
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getKycDocument({
    kycId,
    docId,
  }: {
    kycId: string;
    docId: string;
  }) {
    return urlContentToDataUri(
      `${api.baseUrl}v2/kyc/${kycId}/documents/${docId}`
    );
  }

  export function getDueDiligenceDocument({
    dueDiligenceId,
    docId,
  }: {
    dueDiligenceId: string;
    docId: string;
  }) {
    return urlContentToDataUri(
      `${api.baseUrl}v1/due-diligence/${dueDiligenceId}/documents/${docId}`
    );
  }


  export function downloadKycDocumentById({
    docId,
    fileName,
  }: {
    docId: string;
    fileName: string;
  }) {
    return generateDownload(api, `v2/kyc/documents/${docId}`, {
      verb: "get",
      fileName,
    })();
  }

  export function downloadKycDocument({
    kycId,
    docId,
    fileName,
  }: {
    kycId: string;
    docId: string;
    fileName: string;
  }) {
    return generateDownload(api, `v2/kyc/${kycId}/documents/${docId}`, {
      verb: "get",
      fileName,
    })();
  }

  export function urlContentToDataUri(url: string) {
    return fetch(url, {
      credentials: "include",
    })
      .then(async (response) =>
        response.status === 200
          ? Some({
            type: response.headers.get("content-type"),
            blob: await response.blob(),
          })
          : None
      )
      .then<
        Option<{
          type: string | null;
          data: string;
        }>
      >(
        (body) =>
          new Promise((callback) => {
            body.fold(
              () => callback(None),
              async ({ type, blob }) => {
                let reader = new FileReader();
                reader.onload = function () {
                  callback(
                    this.result
                      ? Some({
                        data: this.result as string,
                        type,
                      })
                      : None
                  );
                };

                // If image is HEIC
                if (type === "application/octet-stream") {
                  const readerResult = await heic2any({
                    blob,
                    toType: "image/jpeg",
                    quality: 1,
                  });

                  Option(readerResult)
                    .map((x) => (Array.isArray(x) ? x[0] : x))
                    .fold(noop, (b) => reader.readAsDataURL(b));
                } else {
                  reader.readAsDataURL(blob);
                }
              }
            );
          })
      );
  }

  export const downloadKycCsv = generateDownload(api, "v2/kyc/search/report", {
    query: {
      selfKyc: false,
    },
    verb: "post",
  });

  export const downloadKybCsv = generateDownload(api, "v1/kyb/report", {
    query: {
      selfKyc: false,
    },
    verb: "post",
  });

  export function getDueDiligenceHeaderImage(merchantId: string) {
    return fetch(
      `${api.baseUrl}v1/due-diligence/public/configs/${merchantId}/images/header`,
      { credentials: "include" }
    );
  }
  export function getDueDiligenceHeaderPath(merchantId: string) {
    return `${api.baseUrl}v1/due-diligence/public/configs/${merchantId}/images/header`;
  }

  export function getSingleDueDiligenceForm(
    id: DueDiligenceVerificationDTO.Responses.DueDiligence.Form.Get.T["id"]
  ): Promise<DueDiligenceVerificationDTO.Responses.DueDiligence.Form.Get.T> {
    return api
      .get(`v1/due-diligence/${id}`)
      .then<DueDiligenceVerificationDTO.Responses.DueDiligence.Form.Get.T>(
        (response) =>
          DueDiligenceVerificationDTO.Responses.DueDiligence.Form.Get.parse(
            response.body
          )
      )
      .catch<DueDiligenceVerificationDTO.Responses.DueDiligence.Form.Get.T>(
        processErrorFromPromise
      );
  }

  export function getPublicSingleDueDiligenceForm(
    id: DueDiligenceVerificationDTO.Responses.DueDiligence.Form.GetPublic.T["id"]
  ): Promise<DueDiligenceVerificationDTO.Responses.DueDiligence.Form.GetPublic.T> {
    return api
      .get(`v1/due-diligence/public/${id}`)
      .then<DueDiligenceVerificationDTO.Responses.DueDiligence.Form.GetPublic.T>(
        (response) => response.body
      )
      .catch<DueDiligenceVerificationDTO.Responses.DueDiligence.Form.GetPublic.T>(
        processErrorFromPromise
      );
  }

  export function postDueDiligenceFormInit(
    id: string,
    data: DueDiligenceVerificationDTO.Requests.DueDiligence.Form.Post.Init.T
  ): Promise<DueDiligenceVerificationDTO.Responses.DueDiligence.Form.Post.Init.T> {
    return api
      .post(`v1/due-diligence/public/configs/${id}/init`)
      .send(data)
      .responseType("json")
      .then((response) => response.body)
      .catch<DueDiligenceVerificationDTO.Responses.DueDiligence.Form.Post.Init.T>(
        processErrorFromPromise
      );
  }

  export function postDueDiligenceFormFinal(
    id: string,
    data: DueDiligenceVerificationDTO.Requests.DueDiligence.Form.Post.Final.T
  ): Promise<DueDiligenceVerificationDTO.Responses.DueDiligence.Form.Post.Final.T> {
    return api
      .post(`v1/due-diligence/public/${id}`)
      .send(data)
      .responseType("json")
      .then((response) => response.body)
      .catch<DueDiligenceVerificationDTO.Responses.DueDiligence.Form.Post.Final.T>(
        processErrorFromPromise
      );
  }

  export function postDueDiligenceFormAttachments(
    id: string,
    fields: DueDiligenceVerificationDTO.Requests.DueDiligence.Form.Post.Attachments.T
  ): Observable<
    ResultWithProgress<DueDiligenceVerificationDTO.Responses.DueDiligence.Form.Post.Attachments.T>
  > {
    return withProgress(
      addFieldsToForm(
        api.post(`v1/due-diligence/public/${id}/attachments`),
        flattenObj(fields, true)
      ),
      (r) =>
        r
          .responseType("json")
          .then((response) => response.body)
          .catch<DueDiligenceVerificationDTO.Responses.DueDiligence.Form.Post.Attachments.T>(
            processErrorFromPromise
          )
    );
  }

  export function postDueDiligenceFormFinalParticipant(
    code: string,
    data: DueDiligenceVerificationDTO.Requests.DueDiligence.Form.Post.Participant.T
  ): Promise<DueDiligenceVerificationDTO.Responses.DueDiligence.Form.Post.Final.T> {
    return api
      .post(`v1/due-diligence/public/participants/${code}`)
      .send(data)
      .responseType("json")
      .then((response) => response.body)
      .catch<DueDiligenceVerificationDTO.Responses.DueDiligence.Form.Post.Final.T>(
        processErrorFromPromise
      );
  }

  export function postDueDiligenceFormAttachmentsParticipant(
    code: string,
    fields: DueDiligenceVerificationDTO.Requests.DueDiligence.Form.Post.ParticipantAttachments.T
  ): Observable<
    ResultWithProgress<DueDiligenceVerificationDTO.Responses.DueDiligence.Form.Post.Attachments.T>
  > {
    return withProgress(
      addFieldsToForm(
        api.post(`v1/due-diligence/public/participants/${code}/attachments`),
        flattenObj(fields, true)
      ),
      (r) =>
        r
          .responseType("json")
          .then((response) => response.body)
          .catch<DueDiligenceVerificationDTO.Responses.DueDiligence.Form.Post.Attachments.T>(
            processErrorFromPromise
          )
    );
  }

  export function getAllDueDiligenceConfigs(
    merchantId: string,
    pagination: Pageable.T<any> = {}
  ): Observable<
    | FetchableError
    | FetchableSuccess<DueDiligenceVerificationDTO.Responses.DueDiligence.Config.GetAll.T>
  > {
    return from(
      api
        .get(`v1/due-diligence/configs/${merchantId}`)
        .query(Pageable.getQueryParam(pagination))
        .then<
          FetchableSuccess<DueDiligenceVerificationDTO.Responses.DueDiligence.Config.GetAll.T>
        >(async (response) => ({
          status: "ready",
          data: {
            ...response.body,
            content: await Promise.all(
              response.body.content.map(
                DueDiligenceVerificationDTO.Responses.DueDiligence.Config.Get
                  .processResponse
              )
            ),
          },
        }))
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getSingleDueDiligenceConfig(
    merchantId: string
  ): Promise<DueDiligenceVerificationDTO.Responses.DueDiligence.Config.Get.T> {
    return api
      .get(`v1/due-diligence/public/configs/${merchantId}`)
      .then<DueDiligenceVerificationDTO.Responses.DueDiligence.Config.Get.T>(
        (response) =>
          DueDiligenceVerificationDTO.Responses.DueDiligence.Config.Get.processResponse(
            response.body
          )
      );
  }

  export function postNewDueDiligenceConfig(
    data: DueDiligenceVerificationDTO.Requests.DueDiligence.Config.Post.T
  ): Observable<
    ResultWithProgress<DueDiligenceVerificationDTO.Responses.DueDiligence.Config.Post.T>
  > {
    return withProgress(
      addFieldsToForm(
        api.post(`v1/due-diligence/`),
        flattenObj({
          ...data,
          expiresAt: Option(data.expiresAt)
            .map((x) => moment(x))
            .map((x) => x.format("YYYY-MM-DD"))
            .get(),
        })
      ),
      (r) =>
        r
          .responseType("json")
          .then((response) => {
            store.dispatch(VerificationActions.resetAllDueDiligenceConfig());
            return response.body;
          })
          .then(
            successNotificationFromPromise("new-due-diligence-form-created")
          )
          .catch<DueDiligenceVerificationDTO.Responses.DueDiligence.Config.Post.T>(
            processErrorFromPromise
          )
    );
  }

  export function updateDueDiligenceConfig(
    id: string,
    data: DueDiligenceVerificationDTO.Requests.DueDiligence.Config.Put.T
  ): Observable<
    ResultWithProgress<DueDiligenceVerificationDTO.Responses.DueDiligence.Config.Put.T>
  > {
    return withProgress(
      addFieldsToForm(
        api.put(`v1/due-diligence/configs/${id}`),
        flattenObj({
          ...data,
          expiresAt: Option(data.expiresAt)
            .map((x) => moment(x))
            .map((x) => x.format("YYYY-MM-DD"))
            .get(),
        })
      ),
      (r) =>
        r
          .responseType("json")
          .then((response) => {
            store.dispatch(VerificationActions.resetAllDueDiligenceConfig());
            return response.body;
          })
          .then(successNotificationFromPromise("due-diligence-form-updated"))
          .catch<DueDiligenceVerificationDTO.Responses.DueDiligence.Config.Put.T>(
            processErrorFromPromise
          )
    );
  }

  export const downloadCertificate = (
    data: Array<typeof CertificateDetails.T>
  ) =>
    generateDownload(api, "v2/kyc/certificate", {
      verb: "post",
      fileName: "certificate.pdf",
      data: {
        includeDetails: data,
      } as VerificationDTO.Requests.DownloadCertificate.T,
    })();

  export const getDeclineCodes: () => Promise<VerificationDTO.Responses.GetDeclineCodes.T> = () =>
    api
      .get(`v2/kyc/decline-codes`)
      .then<VerificationDTO.Responses.GetDeclineCodes.T>((response) => {
        return response.body;
      })
      .catch<VerificationDTO.Responses.GetDeclineCodes.T>(
        processErrorFromPromise
      );

  export const updateKycStatuses: (
    fraudCheckId: string,
    data: KYCVerificationDTO.Requests.UpdateKycStatuses.T
  ) => Promise<void> = (fraudCheckId, data) =>
      api
        .put(`v2/kyc/${fraudCheckId}/statuses`)
        .send(data)
        .then<void>((response) => response.body)
        .then(successNotificationFromPromise("kyc-status-updated"))
        .catch<void>(processErrorFromPromise);

  export const updateKycStatus: (
    fraudCheckId: string,
    data: KYCVerificationDTO.Requests.UpdateKycStatus.T
  ) => Promise<void> = (fraudCheckId, data) =>
      api
        .put(`v2/kyc/${fraudCheckId}/statuses/main`)
        .send(data)
        .then<void>((response) => response.body)
        .then(successNotificationFromPromise("kyc-status-updated"))
        .catch<void>(processErrorFromPromise);

  export const uploadKycDocument: (
    fraudCheckId: string,
    data: KYCVerificationDTO.Requests.UploadKycDocument.T
  ) => Observable<ResultWithProgress<void>> = (fraudCheckId, data) =>
      withProgress(
        addFieldsToForm(
          api.put(`v2/kyc/${fraudCheckId}/documents`),
          flattenObj(data)
        ),
        (r) =>
          r
            .responseType("json")
            .then(successNotificationFromPromise("document-uploaded"))
            .then((response) => response.body)
            .catch<void>(processErrorFromPromise)
      );

  export const updateIndividualKyc: (
    fraudCheckId: string,
    data: KYCVerificationDTO.Requests.UpdateIndividualKYC.T
  ) => Promise<void> = (fraudCheckId, data) =>
      api
        .put(`v2/kyc/${fraudCheckId}`)
        .send(data)
        .then<void>((response) => response.body)
        .then(successNotificationFromPromise("kyc-updated"))
        .catch<void>(processErrorFromPromise);

  export const updateDocumentType: (
    fraudCheckId: string,
    docId: string,
    data: KYCVerificationDTO.Requests.UpdateDocumentType.T
  ) => Promise<void> = (fraudCheckId, docId, data) =>
      api
        .put(`v2/kyc/${fraudCheckId}/documents/${docId}`)
        .send(data)
        .then<void>((response) => response.body)
        .then(successNotificationFromPromise("kyc-updated"))
        .catch<void>(processErrorFromPromise);

  export const resendCallback: (fraudCheckId: string) => Promise<void> = (
    fraudCheckId
  ) =>
    api
      .post(`v2/kyc/${fraudCheckId}/callbacks`)
      .then<void>((response) => response.body)
      .then(successNotificationFromPromise("callback-resent"))
      .catch<void>(processErrorFromPromise);

  export const getKycHistory: (
    fraudCheckId: string
  ) => Promise<KYCVerificationDTO.Responses.KycHistory.T> = (fraudCheckId) =>
      api
        .get(`v2/kyc/${fraudCheckId}/history`)
        .then<KYCVerificationDTO.Responses.KycHistory.T>((response) => {
          return KYCVerificationDTO.Responses.KycHistory.parse(response.body);
        })
        .catch<KYCVerificationDTO.Responses.KycHistory.T>(
          processErrorFromPromise
        );

  export const downloadKYCReport = (fraudCheckId: string) =>
    generateDownload(api, `v2/kyc/${fraudCheckId}/reports/PDF`, {
      verb: "post",
      fileName: "report.pdf",
    })();

  export const validateContactPersonKyc = (
    dueDiligenceId: string,
    emailAddress: string
  ) =>
    api
      .post(`v1/due-diligence/public/${dueDiligenceId}/validate`)
      .send({ emailAddress })
      .then<void>((response) => response.body);

  export const validateParticipantSubmission = (
    dueDiligenceId: string,
    code: string
  ) =>
    api
      .post(`v1/due-diligence/public/${dueDiligenceId}/validate`)
      .send({ code })
      .then<void>((response) => response.body);

  export const validateParticipantEmail = (
    configId: string,
    emailAddress: string
  ) =>
    api
      .post(`v1/due-diligence/public/configs/${configId}/participants/valid`)
      .send({ emailAddress })
      .then<void>((response) => response.body);

  export const getParticipantDetails = (code: string) =>
    api
      .get(`v1/due-diligence/public/participants/${code}`)
      .then<DueDiligenceVerificationDTO.Responses.DueDiligence.Form.GetParticipantDetails.T>(
        (response) => response.body
      );

  export function enableDueDiligenceOptionalField(
    configId: DueDiligenceVerificationDTO.Requests.DueDiligence.OptionalFields.Enable.T["configId"],
    field: DueDiligenceVerificationDTO.Requests.DueDiligence.OptionalFields.Enable.T["field"]
  ): Promise<DueDiligenceVerificationDTO.Responses.DueDiligence.OptionalFields.Enable.T> {
    return api
      .put(`v1/due-diligence/configs/${configId}/fields/${field}`)
      .then<DueDiligenceVerificationDTO.Responses.DueDiligence.OptionalFields.Enable.T>(
        (response) => response.body
      )
      .catch<DueDiligenceVerificationDTO.Responses.DueDiligence.OptionalFields.Enable.T>(
        processErrorFromPromise
      );
  }

  export function disableDueDiligenceOptionalField(
    configId: DueDiligenceVerificationDTO.Requests.DueDiligence.OptionalFields.Disable.T["configId"],
    field: DueDiligenceVerificationDTO.Requests.DueDiligence.OptionalFields.Disable.T["field"]
  ): Promise<DueDiligenceVerificationDTO.Responses.DueDiligence.OptionalFields.Disable.T> {
    return api
      .delete(`v1/due-diligence/configs/${configId}/fields/${field}`)
      .then<DueDiligenceVerificationDTO.Responses.DueDiligence.OptionalFields.Disable.T>(
        (response) => response.body
      )
      .catch<DueDiligenceVerificationDTO.Responses.DueDiligence.OptionalFields.Disable.T>(
        processErrorFromPromise
      );
  }

  export const resendEmailToParticipant = (
    configId: string,
    participantId: string
  ) => resendEmail(configId, "PARTICIPANT", participantId);

  export const resendEmailToContactPerson = (configId: string) =>
    resendEmail(configId, "CONTACT_PERSON");

  function resendEmail(
    configId: string,
    type: "PARTICIPANT" | "CONTACT_PERSON",
    participantId?: string
  ): Promise<DueDiligenceVerificationDTO.Responses.DueDiligence.OptionalFields.Disable.T> {
    return api
      .get(`v1/due-diligence/configs/${configId}/messages/${type}`)
      .query({ participantId })
      .then<DueDiligenceVerificationDTO.Responses.DueDiligence.OptionalFields.Disable.T>(
        (response) => response.body
      )
      .then(successNotificationFromPromise("email-resent"))
      .catch<DueDiligenceVerificationDTO.Responses.DueDiligence.OptionalFields.Disable.T>(
        processErrorFromPromise
      );
  }

  export function getMerchantConfig(
    merchantId: string
  ): Promise<KYCVerificationDTO.Responses.GetMerchantConfig.T> {
    return api
      .get(`merchants/${merchantId}`)
      .then<KYCVerificationDTO.Responses.GetMerchantConfig.T>(
        (response) => response.body
      );
  }

  export function addServiceToMerchantConfig(
    service: string
  ): Promise<KYCVerificationDTO.Responses.GetMerchantConfig.T> {
    const login = store.getState().auth.login;

    if (login.status !== "ready")
      return Promise.reject(new Error("user not logged in"));

    return api
      .put(`merchants/${login.data.accessToken.credentialsId}/kyi/${service}`)
      .then<KYCVerificationDTO.Responses.GetMerchantConfig.T>((response) => {
        store.dispatch(VerificationActions.getCurrentMerchantConfig());
        return response.body;
      });
  }
  export function removeServiceToMerchantConfig(
    service: string
  ): Promise<KYCVerificationDTO.Responses.GetMerchantConfig.T> {
    const login = store.getState().auth.login;

    if (login.status !== "ready")
      return Promise.reject(new Error("user not logged in"));

    return api
      .delete(
        `merchants/${login.data.accessToken.credentialsId}/kyi/${service}`
      )
      .then<KYCVerificationDTO.Responses.GetMerchantConfig.T>((response) => {
        store.dispatch(VerificationActions.getCurrentMerchantConfig());
        return response.body;
      });
  }
}
